import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Calculator, Euro } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty } from "@/pages/shared";
import { uid, withUid } from "@/lib/uid";

const ALL = "__all__";
const blankPeriod = () => ({ zone: ALL, name: "", start: "", end: "", price: "", pct: "", tourist_tax: "", kwh_price: "", children_free: false, items: {} });
const blankItem = () => ({ key: "", label: "", mode: "night", price: "", countable: false, active: true });
const blankPack = () => ({ name: "", items: [], mode: "fixed", unit: "night", price: "", discount_pct: "", include_pitch: false, active: true });
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export default function AdminTariffs() {
  const { t } = useLang();
  const [data, setData] = useState({ zones: [], items: [], periods: [], packages: [], camp_tourist_tax: 0 });
  const [period, setPeriod] = useState(null);
  const [item, setItem] = useState(null);
  const [pack, setPack] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kwh, setKwh] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/tariffs");
      setData(d);
      setKwh(d.camp_kwh_price ?? "");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveKwh = async () => {
    try {
      await api.patch("/settings", { kwh_price: Number(kwh) || 0 });
      await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const itemLabel = (i) => i.label || t(`stay_${i.key}`) || i.key;
  const label = (i) => (i.label ? i.label : i.key === "power6" ? "6 Amp" : i.key === "power10" ? "10 Amp" : itemLabel(i));

  // ---- basistarief per zone
  const saveBase = async (z, b) => {
    try {
      await api.put(`/tariffs/base/${z.code}`, { price: Number(b.price) || 0, tourist_tax: num(b.tourist_tax), children_free: !!b.children_free });
      await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setBase = (code, patch) => setData((d) => ({ ...d, zones: d.zones.map((z) => (z.code === code ? { ...z, base: { ...z.base, ...patch } } : z)) }));

  // ---- periodes
  const savePeriod = async () => {
    setBusy(true);
    const body = {
      zone: period.zone === ALL ? "" : period.zone, name: period.name, start: period.start, end: period.end,
      price: num(period.price), pct: num(period.pct), tourist_tax: num(period.tourist_tax), kwh_price: num(period.kwh_price),
      children_free: period.children_free, items: Object.fromEntries(Object.entries(period.items || {}).filter(([, v]) => v !== "" && v !== null).map(([k, v]) => [k, Number(v)])),
    };
    try {
      if (period.id) await api.put(`/tariffs/periods/${period.id}`, body);
      else await api.post("/tariffs/periods", body);
      setPeriod(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const applyPct = async () => {
    const f = 1 + (Number(period.pct) || 0) / 100;
    if (!period.id) {
      // Nog niet opgeslagen: reken client-side op het basistarief van de gekozen zone
      if (period.zone === ALL) {
        setPeriod({ ...period, price: "", items: Object.fromEntries(data.items.filter((i) => i.price).map((i) => [i.key, Math.round(i.price * f * 100) / 100])) });
        toast.success(t("tariff_calculated"));
        return;
      }
      const base = Number(data.zones.find((z) => z.code === period.zone)?.base?.price) || 0;
      setPeriod({
        ...period, price: Math.round(base * f * 100) / 100,
        items: Object.fromEntries(data.items.filter((i) => i.price).map((i) => [i.key, Math.round(i.price * f * 100) / 100])),
      });
      toast.success(t("tariff_calculated"));
      return;
    }
    try {
      const { data: p } = await api.post(`/tariffs/periods/${period.id}/apply-pct`, { pct: Number(period.pct) || 0, items_too: true });
      setPeriod({ ...period, price: p.price ?? "", items: p.items || {} });
      toast.success(t("tariff_calculated"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delPeriod = async (id) => {
    try { await api.delete(`/tariffs/periods/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- items
  const saveItem = async () => {
    setBusy(true);
    try {
      if (item.id) await api.put(`/tariff-items/${item.id}`, { label: item.label, mode: item.mode, price: Number(item.price) || 0, countable: !!item.countable, active: !!item.active });
      else await api.post("/tariff-items", { key: item.key, label: item.label, mode: item.mode, price: Number(item.price) || 0, countable: !!item.countable, active: true });
      setItem(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const delItem = async (id) => {
    try { await api.delete(`/tariff-items/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- pakketten
  const savePack = async () => {
    setBusy(true);
    const body = {
      name: pack.name, mode: pack.mode, unit: pack.unit || "night", include_pitch: !!pack.include_pitch, active: true,
      price: Number(pack.price) || 0, discount_pct: Number(pack.discount_pct) || 0,
      items: (pack.items || []).filter((l) => l.key).map((l) => ({ key: l.key, qty: Number(l.qty) || 1 })),
    };
    if (!body.name) { toast.error(t("tariff_name_required")); setBusy(false); return; }
    try {
      if (pack.id) await api.put(`/tariff-packages/${pack.id}`, body);
      else await api.post("/tariff-packages", body);
      setPack(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const delPack = async (id) => {
    try { await api.delete(`/tariff-packages/${id}`); await load(); toast.success(t("deleted")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const zoneName = (code) => (code ? (data.zones.find((z) => z.code === code)?.name || code) : t("tariff_all_zones"));

  return (
    <div className="space-y-6" data-testid="tariffs-page">
      {/* Basistarieven */}
      <Panel title={t("tariff_base")} testid="tariff-base-panel">
        <div className="px-5 pt-4 text-sm text-muted-foreground">{t("tariff_base_desc")}</div>
        {data.zones.length === 0 ? <Empty /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("zone")}</TableHead>
                <TableHead className="w-40">{t("tariff_price_night")}</TableHead>
                <TableHead className="w-48">{t("tariff_tourist_tax")}</TableHead>
                <TableHead className="w-40">{t("tariff_children_free")}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.zones.map((z) => (
                <TableRow key={z.code} data-testid={`tariff-base-row-${z.code}`}>
                  <TableCell className="font-semibold text-foreground">{z.name}</TableCell>
                  <TableCell>
                    <Input type="number" min="0" step="0.01" value={z.base.price ?? ""} data-testid={`tariff-base-price-${z.code}`}
                      onChange={(e) => setBase(z.code, { price: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min="0" step="0.01" placeholder={`${t("tariff_default")}: ${money(data.camp_tourist_tax)}`}
                      value={z.base.tourist_tax ?? ""} data-testid={`tariff-base-tax-${z.code}`}
                      onChange={(e) => setBase(z.code, { tourist_tax: e.target.value })} />
                  </TableCell>
                  <TableCell>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" className="accent-primary" checked={!!z.base.children_free} data-testid={`tariff-base-childfree-${z.code}`}
                        onChange={(e) => setBase(z.code, { children_free: e.target.checked })} />
                      {t("tariff_children_free")}
                    </label>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" onClick={() => saveBase(z, z.base)} data-testid={`tariff-base-save-${z.code}`}>{t("save")}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      {/* Elektriciteit */}
      <Panel title={t("tariff_power")} testid="tariff-power-panel">
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-muted-foreground">{t("tariff_power_desc")}</p>
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("tariff_kwh_price")}</Label>
              <Input type="number" min="0" step="0.0001" className="w-40" value={kwh}
                onChange={(e) => setKwh(e.target.value)} data-testid="tariff-kwh-price" />
            </div>
            <Button size="sm" onClick={saveKwh} data-testid="tariff-kwh-save">{t("save")}</Button>
          </div>
        </div>
      </Panel>

      {/* Periodetarieven */}
      <Panel title={t("tariff_periods")} testid="tariff-periods-panel"
        action={<Button onClick={() => setPeriod(blankPeriod())} data-testid="new-period-button"><Plus className="h-4 w-4 mr-1" />{t("new")}</Button>}>
        <div className="px-5 pt-4 text-sm text-muted-foreground">{t("tariff_periods_desc")}</div>
        {data.periods.length === 0 ? <Empty /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("tariff_period_name")}</TableHead>
                <TableHead>{t("zone")}</TableHead>
                <TableHead>{t("tariff_from")}</TableHead>
                <TableHead>{t("tariff_to")}</TableHead>
                <TableHead className="text-right">{t("tariff_price_night")}</TableHead>
                <TableHead className="text-right">{t("tariff_tourist_tax")}</TableHead>
                <TableHead className="text-right">{t("tariff_overrides")}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.periods.map((p) => (
                <TableRow key={p.id} data-testid={`tariff-period-row-${p.id}`}>
                  <TableCell className="font-medium text-foreground">{p.name || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{zoneName(p.zone)}</TableCell>
                  <TableCell className="tabular-nums">{p.start}</TableCell>
                  <TableCell className="tabular-nums">{p.end}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.price != null ? money(p.price) : (p.pct != null ? `+${p.pct}%` : "—")}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{p.tourist_tax == null ? "—" : money(p.tourist_tax)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{Object.keys(p.items || {}).length || "—"}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setPeriod({ ...blankPeriod(), ...p, zone: p.zone || ALL, price: p.price ?? "", pct: p.pct ?? "", tourist_tax: p.tourist_tax ?? "", kwh_price: p.kwh_price ?? "", children_free: !!p.children_free })} data-testid={`edit-period-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => delPeriod(p.id)} data-testid={`delete-period-${p.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      {/* Itemtarieven */}
      <Panel title={t("tariff_items")} testid="tariff-items-panel"
        action={<Button onClick={() => setItem(blankItem())} data-testid="new-tariff-item-button"><Plus className="h-4 w-4 mr-1" />{t("tariff_new_item")}</Button>}>
        <div className="px-5 pt-4 text-sm text-muted-foreground">{t("tariff_items_desc")}</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("tariff_item_label")}</TableHead>
              <TableHead>{t("tariff_item_key")}</TableHead>
              <TableHead>{t("tariff_item_mode")}</TableHead>
              <TableHead className="text-right">{t("tariff_price_night")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((i) => (
              <TableRow key={i.id} data-testid={`tariff-item-row-${i.key}`} className={i.active === false ? "opacity-50" : ""}>
                <TableCell className="font-medium text-foreground flex items-center gap-2">
                  {i.builtin && <img src={`${process.env.PUBLIC_URL || ""}/print/${i.key === "dogs" ? "dog" : i.key.startsWith("power") ? "amp6" : i.key === "adults" ? "adult" : i.key === "children" ? "child" : i.key}.png`} alt="" className="h-5 w-8 object-contain opacity-80" />}
                  {label(i)}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{i.key}</TableCell>
                <TableCell className="text-muted-foreground">{t(`tariff_mode_${i.mode}`)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(i.price)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setItem({ ...blankItem(), ...i, price: i.price ?? "" })} data-testid={`edit-tariff-item-${i.key}`}><Pencil className="h-4 w-4" /></Button>
                  {!i.builtin && <Button variant="ghost" size="sm" onClick={() => delItem(i.id)} data-testid={`delete-tariff-item-${i.key}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>

      {/* Pakketten */}
      <Panel title={t("tariff_packages")} testid="tariff-packages-panel"
        action={<Button onClick={() => setPack(blankPack())} data-testid="new-package-button"><Plus className="h-4 w-4 mr-1" />{t("tariff_new_package")}</Button>}>
        <div className="px-5 pt-4 text-sm text-muted-foreground">{t("tariff_packages_desc")}</div>
        {data.packages.length === 0 ? <Empty /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("tariff_package")}</TableHead>
                <TableHead>{t("tariff_package_content")}</TableHead>
                <TableHead>{t("tariff_package_mode")}</TableHead>
                <TableHead className="text-right">{t("total")}</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.packages.map((p) => (
                <TableRow key={p.id} data-testid={`tariff-package-row-${p.id}`}>
                  <TableCell className="font-medium text-foreground">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {(p.include_pitch ? [t("pitch_fee")] : []).concat((p.items || []).map((l) => `${l.qty} × ${label(data.items.find((i) => i.key === l.key) || { key: l.key })}`)).join(" + ")}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{t(`tariff_mode_${p.mode}`)}</TableCell>
                  <TableCell className="text-right tabular-nums">{p.mode === "fixed" ? `${money(p.price)}${(p.unit || "night") === "night" ? ` / ${t("tariff_mode_night").toLowerCase()}` : ""}` : `-${p.discount_pct}%`}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setPack({ ...blankPack(), ...p, items: withUid(p.items || []), price: p.price ?? "", discount_pct: p.discount_pct ?? "" })} data-testid={`edit-package-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => delPack(p.id)} data-testid={`delete-package-${p.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      {/* Dialog periode */}
      <Dialog open={!!period} onOpenChange={(o) => !o && setPeriod(null)}>
        <DialogContent className="max-w-2xl" data-testid="period-dialog">
          <DialogHeader><DialogTitle className="font-display">{period?.id ? t("edit") : t("tariff_periods")}</DialogTitle></DialogHeader>
          {period && (
            <div className="space-y-3 max-h-[65vh] overflow-auto cb-scroll pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">{t("tariff_period_name")}</Label>
                  <Input required value={period.name} onChange={(e) => setPeriod({ ...period, name: e.target.value })} data-testid="period-name" /></div>
                <div className="space-y-1"><Label className="text-xs">{t("zone")}</Label>
                  <Select value={period.zone} onValueChange={(v) => setPeriod({ ...period, zone: v })}>
                    <SelectTrigger data-testid="period-zone"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>{t("tariff_all_zones")}</SelectItem>
                      {data.zones.map((z) => <SelectItem key={z.code} value={z.code}>{z.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">{t("tariff_from")}</Label>
                  <Input type="date" value={period.start} onChange={(e) => setPeriod({ ...period, start: e.target.value })} data-testid="period-start" /></div>
                <div className="space-y-1"><Label className="text-xs">{t("tariff_to")}</Label>
                  <Input type="date" value={period.end} onChange={(e) => setPeriod({ ...period, end: e.target.value })} data-testid="period-end" /></div>
              </div>
              <div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end rounded-lg border border-border bg-secondary/30 p-3">
                <div className="space-y-1"><Label className="text-xs">{t("tariff_pct")}</Label>
                  <Input type="number" step="0.1" value={period.pct} onChange={(e) => setPeriod({ ...period, pct: e.target.value })} data-testid="period-pct" /></div>
                <div className="space-y-1"><Label className="text-xs">{t("tariff_price_night")}</Label>
                  <Input type="number" min="0" step="0.01" value={period.price} onChange={(e) => setPeriod({ ...period, price: e.target.value })} data-testid="period-price" /></div>
                <Button type="button" variant="outline" onClick={applyPct} data-testid="period-apply-pct"><Calculator className="h-4 w-4 mr-1" />{t("tariff_apply_pct")}</Button>
                <p className="col-span-3 text-[11px] text-muted-foreground">{t("tariff_pct_hint")}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">{t("tariff_tourist_tax")}</Label>
                  <Input type="number" min="0" step="0.01" placeholder={t("tariff_default")} value={period.tourist_tax} onChange={(e) => setPeriod({ ...period, tourist_tax: e.target.value })} data-testid="period-tax" /></div>
                <div className="space-y-1"><Label className="text-xs">{t("tariff_kwh_price")}</Label>
                  <Input type="number" min="0" step="0.0001" placeholder={t("tariff_default")} value={period.kwh_price} onChange={(e) => setPeriod({ ...period, kwh_price: e.target.value })} data-testid="period-kwh" /></div>
                <label className="flex items-center gap-2 text-sm self-end py-2">
                  <input type="checkbox" className="accent-primary" checked={!!period.children_free} onChange={(e) => setPeriod({ ...period, children_free: e.target.checked })} data-testid="period-childfree" />
                  {t("tariff_children_free")}
                </label>
              </div>
              <div>
                <Label className="text-xs">{t("tariff_overrides")}</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {data.items.map((i) => (
                    <div key={i.key} className="flex items-center gap-2">
                      <span className="text-xs flex-1 truncate">{label(i)}</span>
                      <Input type="number" min="0" step="0.01" className="h-8 w-24 text-xs" placeholder={money(i.price)} data-testid={`period-item-${i.key}`}
                        value={period.items?.[i.key] ?? ""} onChange={(e) => setPeriod({ ...period, items: { ...(period.items || {}), [i.key]: e.target.value } })} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPeriod(null)}>{t("cancel")}</Button>
            <Button onClick={savePeriod} disabled={busy} data-testid="period-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog item */}
      <Dialog open={!!item} onOpenChange={(o) => !o && setItem(null)}>
        <DialogContent className="max-w-md" data-testid="tariff-item-dialog">
          <DialogHeader><DialogTitle className="font-display">{item?.id ? t("edit") : t("tariff_new_item")}</DialogTitle></DialogHeader>
          {item && (
            <div className="space-y-3">
              {!item.id && (
                <div className="space-y-1"><Label className="text-xs">{t("tariff_item_key")}</Label>
                  <Input required value={item.key} onChange={(e) => setItem({ ...item, key: e.target.value })} data-testid="tariff-item-key" /></div>
              )}
              <div className="space-y-1"><Label className="text-xs">{t("tariff_item_label")}</Label>
                <Input required value={item.label} onChange={(e) => setItem({ ...item, label: e.target.value })} data-testid="tariff-item-label" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">{t("tariff_item_mode")}</Label>
                  <Select value={item.mode} onValueChange={(v) => setItem({ ...item, mode: v })}>
                    <SelectTrigger data-testid="tariff-item-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="night">{t("tariff_mode_night")}</SelectItem>
                      <SelectItem value="once">{t("tariff_mode_once")}</SelectItem>
                      <SelectItem value="month">{t("tariff_mode_month")}</SelectItem>
                      <SelectItem value="month_prorata">{t("tariff_mode_month_prorata")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">{t("tariff_price_night")}</Label>
                  <Input type="number" min="0" step="0.01" value={item.price} onChange={(e) => setItem({ ...item, price: e.target.value })} data-testid="tariff-item-price" /></div>
              </div>
              {!item.builtin && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" className="accent-primary" checked={!!item.countable} onChange={(e) => setItem({ ...item, countable: e.target.checked })} data-testid="tariff-item-countable" />
                  {t("tariff_countable")}
                </label>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="accent-primary" checked={item.active !== false} onChange={(e) => setItem({ ...item, active: e.target.checked })} data-testid="tariff-item-active" />
                {t("active")}
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setItem(null)}>{t("cancel")}</Button>
            <Button onClick={saveItem} disabled={busy} data-testid="tariff-item-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog pakket */}
      <Dialog open={!!pack} onOpenChange={(o) => !o && setPack(null)}>
        <DialogContent className="max-w-lg" data-testid="package-dialog">
          <DialogHeader><DialogTitle className="font-display">{pack?.id ? t("edit") : t("tariff_new_package")}</DialogTitle></DialogHeader>
          {pack && (
            <div className="space-y-3 max-h-[60vh] overflow-auto cb-scroll pr-1">
              <div className="space-y-1"><Label className="text-xs">{t("tariff_package")}</Label>
                <Input required value={pack.name} onChange={(e) => setPack({ ...pack, name: e.target.value })} data-testid="package-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">{t("tariff_package_mode")}</Label>
                  <Select value={pack.mode} onValueChange={(v) => setPack({ ...pack, mode: v })}>
                    <SelectTrigger data-testid="package-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">{t("tariff_mode_fixed")}</SelectItem>
                      <SelectItem value="discount">{t("tariff_mode_discount")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {pack.mode === "fixed" ? (
                  <div className="space-y-1"><Label className="text-xs">{t("tariff_package_total")}</Label>
                    <Input type="number" min="0" step="0.01" value={pack.price} onChange={(e) => setPack({ ...pack, price: e.target.value })} data-testid="package-price" /></div>
                ) : (
                  <div className="space-y-1"><Label className="text-xs">{t("tariff_discount_pct")}</Label>
                    <Input type="number" min="0" step="0.1" value={pack.discount_pct} onChange={(e) => setPack({ ...pack, discount_pct: e.target.value })} data-testid="package-discount" /></div>
                )}
                {pack.mode === "fixed" && (
                  <div className="space-y-1 col-span-2"><Label className="text-xs">{t("tariff_item_mode")}</Label>
                    <Select value={pack.unit || "night"} onValueChange={(v) => setPack({ ...pack, unit: v })}>
                      <SelectTrigger data-testid="package-unit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="night">{t("tariff_mode_night")}</SelectItem>
                        <SelectItem value="stay">{t("tariff_mode_stay")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="accent-primary" checked={!!pack.include_pitch} onChange={(e) => setPack({ ...pack, include_pitch: e.target.checked })} data-testid="package-include-pitch" />
                {t("tariff_include_pitch")}
              </label>
              <div className="space-y-2">
                <Label className="text-xs">{t("tariff_package_content")}</Label>
                {(pack.items || []).map((l, idx) => (
                  <div key={l._uid} className="grid grid-cols-[1fr_80px_auto] gap-2 items-center" data-testid={`package-line-${idx}`}>
                    <Select value={l.key} onValueChange={(v) => setPack({ ...pack, items: pack.items.map((x, j) => (j === idx ? { ...x, key: v } : x)) })}>
                      <SelectTrigger><SelectValue placeholder={t("tariff_item_label")} /></SelectTrigger>
                      <SelectContent>
                        {data.items.map((i) => <SelectItem key={i.key} value={i.key}>{label(i)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input type="number" min="1" value={l.qty} onChange={(e) => setPack({ ...pack, items: pack.items.map((x, j) => (j === idx ? { ...x, qty: e.target.value } : x)) })} />
                    <Button variant="ghost" size="sm" onClick={() => setPack({ ...pack, items: pack.items.filter((_, j) => j !== idx) })}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setPack({ ...pack, items: [...(pack.items || []), { _uid: uid(), key: "", qty: 1 }] })} data-testid="package-add-line">
                  <Plus className="h-4 w-4 mr-1" />{t("add")}
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground flex items-start gap-1"><Euro className="h-3.5 w-3.5 mt-[1px]" />{t("tariff_package_hint")}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPack(null)}>{t("cancel")}</Button>
            <Button onClick={savePack} disabled={busy} data-testid="package-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
